import { OverlayLayerContext, useOverlayLayer } from "./OverlayLayer";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { X } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "../lib/cn";

const Root = BaseDialog.Root;
const Trigger = BaseDialog.Trigger;
const Close = BaseDialog.Close;

export interface DialogContentProps extends Omit<BaseDialog.Popup.Props, "className"> {
  className?: string;
  /** Renders an X button in the top-right corner. Default true. */
  showClose?: boolean;
}

export const dialogBackdropClassName =
  "fixed inset-0 z-overlay bg-black/40 transition-opacity duration-base data-starting-style:opacity-0 data-ending-style:opacity-0";

function Content({ className, children, showClose = true, ...props }: DialogContentProps) {
  const layer = useOverlayLayer("modal");
  return (
    <OverlayLayerContext value={layer}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop
          style={{ zIndex: `calc(${layer} - 1)` }}
          data-slot="dialog-backdrop"
          className={dialogBackdropClassName}
        />
        <BaseDialog.Popup
          data-slot="dialog-content"
          className={cn(
            "fixed top-1/2 left-1/2 z-modal flex max-h-[calc(100dvh-2rem-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] w-[calc(100%-2rem-env(safe-area-inset-left,0px)-env(safe-area-inset-right,0px))] max-w-md overflow-y-auto overscroll-contain -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-lg border border-border bg-surface-raised p-5 text-fg shadow-lg outline-none transition-[opacity,scale] duration-base ease-enter data-starting-style:scale-[0.97] data-starting-style:opacity-0 data-ending-style:scale-[0.97] data-ending-style:opacity-0",
            className,
          )}
          {...props}
          style={{ zIndex: layer, ...props.style }}
        >
          {children}
          {showClose && (
            <BaseDialog.Close
              data-slot="dialog-close"
              aria-label="Close"
              className="absolute top-3 right-3 inline-flex size-7 items-center justify-center rounded-md text-fg-muted transition-colors duration-fast hover:bg-fg/6 hover:text-fg focus-ring"
            >
              <X className="size-4" />
            </BaseDialog.Close>
          )}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </OverlayLayerContext>
  );
}

function Header({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-1 pr-6", className)}
      {...props}
    />
  );
}

function Footer({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

function Title({ className, ...props }: BaseDialog.Title.Props & { className?: string }) {
  return (
    <BaseDialog.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-6 font-semibold text-fg", className)}
      {...props}
    />
  );
}

function Description({
  className,
  ...props
}: BaseDialog.Description.Props & { className?: string }) {
  return (
    <BaseDialog.Description
      data-slot="dialog-description"
      className={cn("text-sm text-fg-muted", className)}
      {...props}
    />
  );
}

/**
 * Modal dialog. Anatomy:
 * `Dialog.Root > Dialog.Trigger + Dialog.Content > Dialog.Header > Dialog.Title / Dialog.Description, ..., Dialog.Footer`.
 */
export const Dialog = { Root, Trigger, Content, Header, Footer, Title, Description, Close };
